// Compact top-right session control. Shows who is signed in and a rough
// countdown to the refresh-token expiry (Cognito default: 30 days from
// last sign-in). Menu lets the user manually refresh their session or sign
// out.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { forceRefreshTokens, sessionInfo, signOut, SessionInfo } from "../lib/auth";

// Cognito app-client refresh-token lifetime for this pool. Kept in sync with
// the CDK config in infra/cdk/config/poc.ts. This is used for the visible
// countdown chip; it does not enforce any policy client-side.
const REFRESH_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

export function SessionChip() {
  const nav = useNavigate();
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<null | "refresh" | "signout">(null);
  const [flash, setFlash] = useState<string>("");
  const menuRef = useRef<HTMLDivElement>(null);

  // Initial load, and refresh whenever we might have signed in/out from another
  // tab or an API-driven refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await sessionInfo();
      if (!cancelled) setInfo(s);
    })();
    return () => { cancelled = true; };
  }, []);

  // Close the menu on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function onRefresh() {
    setBusy("refresh");
    setFlash("");
    try {
      const token = await forceRefreshTokens();
      if (!token) {
        setFlash("Refresh failed. Please sign out and sign back in.");
      } else {
        const s = await sessionInfo();
        setInfo(s);
        setFlash("Session refreshed.");
        setTimeout(() => setFlash(""), 2000);
      }
    } finally {
      setBusy(null);
    }
  }

  async function onSignOut() {
    setBusy("signout");
    try {
      await signOut();
      nav("/", { replace: true });
    } finally {
      setBusy(null);
    }
  }

  if (!info) {
    return (
      <button className="btn-ghost text-xs px-3 py-2" disabled>
        Loading session
      </button>
    );
  }

  const expiryLabel = formatRefreshExpiry(info.refreshIssuedAtMs);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-white hover:bg-surface transition-colors"
        title="Session"
      >
        <span className="w-2 h-2 rounded-full bg-verified inline-block" aria-hidden="true" />
        {/* Full email on md+; a short fixed label on smaller screens so the
            header stays compact regardless of email length. */}
        <span className="text-xs text-ink hidden md:inline">{info.email}</span>
        <span className="text-xs text-ink inline md:hidden">Account</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-border rounded-md shadow-lg z-10 p-3 space-y-2">
          <div className="text-xs text-ink-faint uppercase tracking-widest">Signed in as</div>
          <div className="text-sm text-ink break-all">{info.email}</div>
          <div className="text-xs text-ink-soft">
            Session {expiryLabel === "\u2014" ? "active" : expiryLabel}. You will be signed out automatically when it expires.
          </div>
          {flash && (
            <div className="text-xs text-verified">{flash}</div>
          )}
          <div className="flex gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onRefresh}
              disabled={busy !== null}
              className="btn-secondary text-xs px-3 py-1 flex-1"
            >
              {busy === "refresh" ? "Refreshing\u2026" : "Refresh session"}
            </button>
            <button
              type="button"
              onClick={onSignOut}
              disabled={busy !== null}
              className="btn-ghost text-xs px-3 py-1 flex-1"
            >
              {busy === "signout" ? "Signing out\u2026" : "Sign out"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatRefreshExpiry(issuedAtMs: number | null): string {
  if (!issuedAtMs) return "\u2014";
  const remainingMs = issuedAtMs + REFRESH_LIFETIME_MS - Date.now();
  if (remainingMs <= 0) return "expires now";
  const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  if (days >= 2) return `expires in ${days}d`;
  const hours = Math.floor(remainingMs / (60 * 60 * 1000));
  if (hours >= 2) return `expires in ${hours}h`;
  const mins = Math.max(1, Math.floor(remainingMs / (60 * 1000)));
  return `expires in ${mins}m`;
}
